/**
 * <galaxy-collections-reveal>: touch behaviour of the collection tiles (styles in sections/galaxy-collections.liquid).
 * - Phones (swipe rows): the dash (.galaxy-dash) of the tile at the start of the row is marked while swiping, and
 *   tapping a dash glides to its tile. When a swipe comes to rest, that tile shows its lifestyle photo
 *   (class .is-revealed). Nothing changes on the tiles while the finger or the row is still moving.
 *   "At rest" = the row has snapped onto a tile and stopped moving (or the browser's scrollend). iPhones
 *   keep sending sub-pixel scroll events for about a second after the row looks still, so waiting for
 *   the events to stop revealed the photo late, often just as the shopper tapped the tile.
 * - Tablets (grid, nothing to swipe): touching a tile shows its lifestyle photo; a tap still opens it.
 * One tile at a time; it turns back when it leaves the screen. Mouse users get :hover instead.
 */
const TILE = '.galaxy-collections__tile--has-hover';
const SETTLE_MS = 160;
/** Within this many pixels the row counts as snapped and still. */
const REST_PX = 1;

class GalaxyCollectionsReveal extends HTMLElement {
  connectedCallback() {
    /** @type {Element | null} */
    this.current = null;
    /** @type {Map<Element, number>} */
    this.settleTimers = new Map();
    /** @type {WeakMap<Element, number>} Last scroll position of each row. */
    this.lastLeft = new WeakMap();
    this.touching = false;
    this.rows = [...this.querySelectorAll('.galaxy-collections__row')];

    this.onClick = (/** @type {MouseEvent} */ event) => {
      const dot = event.target instanceof Element ? event.target.closest('.galaxy-dash') : null;
      const row = dot?.parentElement?.previousElementSibling;
      const tile = row?.children[Number(dot?.dataset.go)];
      if (!tile) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollBy({
        left: tile.getBoundingClientRect().left - row.getBoundingClientRect().left,
        behavior: smooth ? 'smooth' : 'auto',
      });
    };
    this.onPointerDown = (/** @type {PointerEvent} */ event) => {
      if (event.pointerType === 'mouse' || !(event.target instanceof Element)) return;
      const tile = event.target.closest(TILE);
      const row = tile?.closest('.galaxy-collections__row');
      if (tile && row && !this.#swipes(row)) this.#reveal(tile);
    };
    // Scroll events don't bubble: listen in the capture phase to hear every row.
    this.onScroll = (/** @type {Event} */ event) => {
      const row = event.target;
      if (!(row instanceof Element) || !row.classList.contains('galaxy-collections__row')) return;
      this.#dots(row);
      clearTimeout(this.settleTimers.get(row));
      const moved = Math.abs(row.scrollLeft - (this.lastLeft.get(row) ?? NaN));
      this.lastLeft.set(row, row.scrollLeft);
      if (!this.touching && moved < REST_PX && this.#snapped(row)) {
        this.#settle(row);
        return;
      }
      this.settleTimers.set(row, setTimeout(() => this.#settle(row), SETTLE_MS));
    };
    this.onScrollEnd = (/** @type {Event} */ event) => {
      const row = event.target;
      if (this.touching || !(row instanceof Element) || !row.classList.contains('galaxy-collections__row')) return;
      clearTimeout(this.settleTimers.get(row));
      this.#settle(row);
    };
    this.onTouchStart = () => {
      this.touching = true;
    };
    this.onTouchEnd = () => {
      this.touching = false;
    };
    this.onResize = () => this.rows.forEach((row) => this.#dots(row));

    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    this.addEventListener('scrollend', this.onScrollEnd, { capture: true, passive: true });
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();

    // Back to the product photo once a revealed tile is scrolled off screen (sideways or down the page).
    this.observer = new IntersectionObserver((entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.target === this.current) this.#reveal(null);
      })
    );
    this.querySelectorAll(TILE).forEach((tile) => this.observer.observe(tile));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('scroll', this.onScroll, { capture: true });
    this.removeEventListener('scrollend', this.onScrollEnd, { capture: true });
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
    this.removeEventListener('touchcancel', this.onTouchEnd);
    window.removeEventListener('resize', this.onResize);
    this.settleTimers?.forEach((timer) => clearTimeout(timer));
    this.observer?.disconnect();
  }

  /** @param {Element} row */
  #swipes(row) {
    return row.scrollWidth > row.clientWidth + 1;
  }

  /** The tile closest to the start of the row (the one a swipe snaps to). @param {Element} row */
  #startTile(row) {
    const start = row.getBoundingClientRect().left;
    let best = null;
    let distance = Infinity;
    for (const tile of row.children) {
      const d = Math.abs(tile.getBoundingClientRect().left - start);
      if (d < distance) [best, distance] = [tile, d];
    }
    return best;
  }

  /** The row sits on a snap point: a tile lines up with its start, or it is scrolled to the end. @param {Element} row */
  #snapped(row) {
    if (Math.abs(row.scrollLeft) >= row.scrollWidth - row.clientWidth - REST_PX) return true;
    const tile = this.#startTile(row);
    return !!tile && Math.abs(tile.getBoundingClientRect().left - row.getBoundingClientRect().left) < REST_PX;
  }

  /** Show the row's dashes only when it can be swiped, and mark the current tile's. @param {Element} row */
  #dots(row) {
    const dots = row.nextElementSibling;
    if (!(dots instanceof HTMLElement) || !dots.classList.contains('galaxy-collections__dots')) return;
    dots.hidden = !this.#swipes(row);
    if (dots.hidden) return;
    const atEnd = Math.abs(row.scrollLeft) >= row.scrollWidth - row.clientWidth - 2;
    const index = atEnd ? row.children.length - 1 : [...row.children].indexOf(this.#startTile(row));
    [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }

  /** The swipe has stopped: reveal the tile that sits at the start of the row. @param {Element} row */
  #settle(row) {
    this.settleTimers.delete(row);
    const tile = this.#startTile(row);
    this.#reveal(tile?.matches(TILE) ? tile : null);
  }

  /** @param {Element | null} tile */
  #reveal(tile) {
    if (tile === this.current) return;
    this.current?.classList.remove('is-revealed');
    tile?.classList.add('is-revealed');
    this.current = tile;
  }
}

if (!customElements.get('galaxy-collections-reveal')) {
  customElements.define('galaxy-collections-reveal', GalaxyCollectionsReveal);
}
